import type { GeoItemElevationAccuracy } from '@civilgrid/utils/src/geoItems/elevationDisclosure';
import type { UtilityMeasurementEntry } from '@civilgrid/utils/src/geoItems/utilityMeasurements';
import type { Units } from '@turf/turf';
import type {
  Feature,
  FeatureCollection,
  GeoJSON,
  GeoJsonProperties,
  Geometry,
  LineString,
  MultiLineString,
  MultiPolygon,
  Point,
  Polygon,
} from 'geojson';
import type { DateTime } from 'luxon';
import type { Action } from '~/authorizations';
import type { OrgSnippet } from '~/pages/auth/common';
import type { DocType } from '~/pages/projectDetails/landRights/types';

export const TRANSPARENT = 'transparent';
export const CIVIL_DRAW_ROUTE = 'civil-draw';
export const DETAILS_ROUTE = 'details';
export const INTELLIGENCE_ROUTE = 'intelligence';

export const CG_PROJ_ID = 'cg_projid';
export const CG_ORG_ID = 'cg_orgid';
export const REPORT_ALL_ID = 'robust_id';

export enum MapStyle {
  streets = 'mapbox://styles/mapbox/streets-v12',
  outdoors = 'mapbox://styles/mapbox/outdoors-v12',
  light = 'mapbox://styles/mapbox/light-v11',
  dark = 'mapbox://styles/mapbox/dark-v11',
  satellite = 'mapbox://styles/mapbox/satellite-streets-v12',
  satelliteWithoutStreets = 'mapbox://styles/mapbox/satellite-v9',
  downloadPreviewDark = 'mapbox://styles/agoramaps21/clyj37ccu01q601qrgq0e2gdz',
  downloadPreviewWhite = 'mapbox://styles/agoramaps21/clyj3cvd001sc01qo8gpucfah',
  civilGridLight = 'mapbox://styles/agoramaps21/cmht8vl7c00aw01rz89hq7xqc',
  civilGridDark = 'mapbox://styles/agoramaps21/cmi3nujy400pn01sc147vfoub',
}

export const defaultBasemapStyle = MapStyle.civilGridLight;

export enum SortOrder {
  CreatedAt = 'Created At',
  Title = 'Title',
  Type = 'Type',
}

export interface ObjectFile {
  id: string;
  uploadedBy: string;
  uploadedAt: string;
  notes: string;
  extension: string;
  path: string;
  name: string;
  type: string;
  dataRequestV2?: DataRequestV2['id'];
  civilSearchMessage?: CivilSearchMessage['id'];
}

export interface TLoadingState {
  isLoading: true;
}

export interface TLoadedState {
  isLoading: false;
}

export const LoadingState: TLoadingState = { isLoading: true };

export interface OrganizationRegion {
  name: string;
  region: GeoJSON;
}

export interface SubscriptionProjectType {
  urban: { unit: number; cost: number };
  rural: { unit: number; cost: number };
}

export interface BillingTierCost {
  polygon: {
    urban: { unit: number; cost: number };
    rural: { unit: number; cost: number };
  };
  linear: {
    urban: { unit: number; cost: number };
    rural: { unit: number; cost: number };
  };
  circle: {
    urban: { unit: number; cost: number };
    rural: { unit: number; cost: number };
  };
  intersection: {
    urban: { unit: number; cost: number };
    rural: { unit: number; cost: number };
  };
}

export interface Subscription {
  startDate: Date;
  limits: {
    totalProjects: number;
    projectsPerMonth: number;
    downloadsPerMonth: number;
    mapsPerMonth: number;
    titlePerMonth: number;
    otherPerMonth: number;
  };
  zoningAccess: 'none' | 'basic' | 'premium';
  zoningCost: number;
  projectCost: {
    // draft will always be 0
    proposal: BillingTierCost;
    project: BillingTierCost;
    projectPlus: BillingTierCost;
  };
  sfdcAccountId?: string | null;
  sfdcOpportunityId?: string | null;
  featureAccess?: Partial<Record<string, ('draft' | 'proposal' | 'project' | 'projectPlus')[]>>;
  enabledBillingTiers?: Partial<Record<'draft' | 'proposal' | 'project' | 'projectPlus', boolean>>;
}

export interface SubscriptionDetail extends Subscription {
  id: string;
  organizationId: string;
  endDate?: Date | string | null;
  status: string;
  createdById?: string | null;
  createdBy?: {
    id: string;
    email: string;
    name: string;
  } | null;
  updatedById?: string | null;
  updatedBy?: {
    id: string;
    email: string;
    name: string;
  } | null;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

export interface Organization {
  id: string;
  name: string;
  regions: OrganizationRegion[];
  zoningAccess: 'none' | 'basic' | 'premium';
  mobileReportType: MobileReportType;
}

interface WebhookResult {
  status: string;
  date: Date;
  inquiryId: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isApproved: boolean;
  isOnboarded: boolean;
  organizations: Organization[];
  availableOrgs: OrgSnippet[];
  memberships?: Array<{
    organizationId: string;
    organizationName: string;
    role: 'owner' | 'member' | 'contributor' | 'contractor' | 'viewer';
    status: 'active' | 'suspended';
  }>;
  verificationSent: boolean;
  verificationApprovalWebhookReceived: [WebhookResult];
  profile?: {
    title: string;
    linkedInUrl: string;
  };
  status: 'active' | 'archived' | 'suspended' | 'invited';
  createdAt: Date;
  knockToken?: string;
  preferences: {
    projectBookmarks: Array<Project['id']>;
  };
}

export interface ObjectBase {
  id: string;
  sortOrder: string;
  title: string;
  type: string;
  description: string;
  createdBy: {
    name: string;
    email?: string;
  };
  updatedBy: {
    name: string;
  };
  properties: { [key: string]: number | string | boolean };
  createdAt?: DateTime;
  files?: ObjectFile[];
  interactive?: {
    vertices?: boolean;
    lines?: boolean;
  };
}

export interface PolygonObject extends ObjectBase {
  type: 'polygon';
  geometry: GeoJSON.Polygon;
  properties: {
    color: string;
    fillOpacity: number;
    strokeOpacity: number;
    strokeWidth: number;
    strokeStyle: 'solid' | 'dashed';
    showArea: boolean;
    showTitle: boolean;
    labelAlwaysVisible: boolean;
  };
}

export interface LineObject extends ObjectBase {
  type: 'line';
  geometry: GeoJSON.LineString;
  properties: {
    buffer?: number; // in feet
    color: string;
    strokeOpacity: number;
    strokeWidth: number;
    strokeStyle: 'solid' | 'dashed';
    showLength: boolean;
    showTitle: boolean;
    labelAlwaysVisible: boolean;
  };
}

export interface ArrowBase extends ObjectBase {
  type: 'arrow';
  geometry: GeoJSON.LineString;
  properties: {
    subtype: 'standard' | 'metadata';
  };
}

export interface ArrowStandard extends ArrowBase {
  properties: {
    subtype: 'standard';
    color: string;
    strokeOpacity: number;
    strokeWidth: number;
    strokeStyle: 'solid' | 'dashed';
    showTitle: boolean;
    labelAlwaysVisible: boolean;
    showAsLeader: boolean;
    showDescription: boolean;
  };
}

export interface ArrowMetadata extends ArrowBase {
  properties: {
    subtype: 'metadata';
    color: string;
    strokeOpacity: number;
    strokeWidth: number;
    strokeStyle: 'solid' | 'dashed';
    font: string;
    minZoom: number;
  };
}

export type ArrowObject = ArrowStandard | ArrowMetadata;

export interface MarkerObject extends ObjectBase {
  type: 'marker';
  geometry: GeoJSON.Point;
  properties: {
    color: string;
    showTitle: boolean;
  };
}

// description will be used as the display field
export interface TextObject extends ObjectBase {
  type: 'text';
  geometry: GeoJSON.Point;
  properties: {
    color: string;
    backgroundColor: string;
    borderColor: string;
    fontSize: number; // in pixels
    createdAtZoom: number;
    rotation: number;
  };
}

export interface CommentGroupObject extends ObjectBase {
  type: 'comment';
  geometry: GeoJSON.Point;
  commentGroup: CommentObject[];
  updatedAt: DateTime;
  status: string;
}

export interface TextObjectV2 extends ObjectBase {
  type: 'textV2';
  geometry: GeoJSON.Polygon;
  properties: {
    color: string;
    backgroundColor: string;
    borderColor: string;
  };
}

export interface ImageObject extends ObjectBase {
  type: 'image';
  geometry: GeoJSON.Polygon;
  properties: {
    color: string;
    fillOpacity: number;
    strokeOpacity: number;
    strokeWidth: number;
    strokeStyle: 'solid' | 'dashed';
    showBorder: boolean;
    minimized: boolean;
    showTitle: boolean;
  };
}

export interface StampObject extends ObjectBase {
  type: 'stamp';
  geometry: GeoJSON.Polygon;
  properties: {
    fillOpacity: number;
    path: string;
    subtype?: 'emoji';
    emoji?: string;
  };
}

export interface StreetViewMarkerObject extends ObjectBase {
  type: 'streetViewMarker';
  geometry: GeoJSON.Point;
  properties: {
    heading: number;
    pitch: number;
    zoom: number;
  };
}

export interface CircleObject extends ObjectBase {
  type: 'circle';
  geometry: GeoJSON.Point;
  properties: {
    radius: number; // in feet
    color: string;
    fillOpacity: number;
    strokeOpacity: number;
    strokeWidth: number;
    strokeStyle: 'solid' | 'dashed';
    showArea: boolean;
    showRadius: boolean;
    showTitle: boolean;
    labelAlwaysVisible: boolean;
    useMilesToDisplayRadius: boolean;
  };
}

export type MapObject =
  | PolygonObject
  | LineObject
  | MarkerObject
  | TextObject
  | CommentGroupObject
  | TextObjectV2
  | ImageObject
  | StampObject
  | StreetViewMarkerObject
  | ArrowObject
  | CircleObject;

export interface CommentObject {
  id: string;
  createdBy: {
    id: string;
    name: string;
    email: string;
  };
  updatedBy?: {
    id: string;
    name: string;
    email: string;
  };
  createdAt: DateTime;
  comment: string;
  status: string;
  updatedAt: DateTime;
}

export interface MapVersion {
  id: string;
  name: string;
  createdAt: DateTime;
  updatedAt: DateTime;
  objects: {
    [key: string]: MapObject;
  };
}

export type UnitSystem = 'imperial' | 'metric';

export interface Map {
  id: string;
  name: string;
  image: string;
  unitSystem: UnitSystem;
  baseMap: MapStyle;
  createdAt: DateTime;
  createdBy: {
    name: string;
  };
  updatedAt: DateTime;
  updatedBy: {
    name: string;
  };
}

export interface MapDetail extends Map {
  objects: {
    [key: string]: MapObject;
  };
}

export type MyMap = Map;

export interface TitleJson {
  Date: string;
  Parties: string;
  Party1: string;
  Party2: string;
  DocType: DocType;
  DocId: string;
  CmtId: string;
}

interface ParcelDetails {
  parcelId: string;
  owner: string;
  physzip: string;
  titleJson: string;
}

export interface MemberUser {
  email: string;
  id: string;
  name: string;
  status: string;
  firstName: string;
  lastName: string;
}

export interface Member {
  id: string;
  role: string;
  status: string;
  user: MemberUser;
  notifications: {
    civilsearch: {
      projectWeeklyDigest: boolean;
      newRequestsCreated: boolean;
      newMessagesCreated: boolean;
      requestStatusUpdates: boolean;
      requestDocumentsAdded: boolean;
      requestCompleteOnly: boolean;
    };
    intelligence: {
      newConflict: boolean;
      clearedConflict: boolean;
      conflictsChanged?: boolean; // Missing on members added before it existed; treat missing as on.
    };
  };
}

export interface OrganizationRole {
  id: string;
  roleId: string;
  name: string;
  description: string;
  actions: Action[];
}

export interface RegionWithId extends Omit<Region, '_id'> {
  id: string;
}

export enum PlanType {
  Trial = 'trial',
  Tier1 = 'tier1',
  Tier2 = 'tier2',
  Tier3 = 'tier3',
  Partner = 'partner',
  DataRequest = 'dataRequest',
  Pilot = 'pilot',
  Sandbox = 'sandbox',
}

export enum ProjectPhase {
  Proposed = 'Proposed',
  Scoping = 'Scoping',
  Planning = 'Planning',
  Engineering = 'Engineering',
  PreConstruction = 'Pre Construction',
  Construction = 'Construction',
  CloseOut = 'Close Out',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
}

export interface LinkedOrganizationDetail {
  id: string;
  name: string;
}

export interface OrganizationDetail {
  name: string;
  plan: string;
  planStatus: string;
  dataRefIds: string[];
  docLimits: {
    maps: number;
    title: number;
    other: number;
    _id: string;
  };
  projectDownloadLimit: number;
  members: Member[];
  roles: OrganizationRole[];
  isManaged: boolean;
  isInternal: boolean;
  status: string;
  telcoAccess: boolean;
  connectbaseLookupEnabled: boolean;
  mobileReportType: MobileReportType;
  regions: RegionWithId[];
  createdAt: string;
  updatedAt: string;
  planExpires: string;
  specialDatasets: any[];
  specialFeatures: string[];
  /**
   * DB-derived layer config delivered at bootstrap (CG-432 / CG-587). Absent when the
   * server's snapshot read produced nothing — the client then uses the legacy TS tree.
   * The server delivers `toc` and `specialDatasets` only; `digitization` and
   * `layerIdToDownloadLayer` stay client-side.
   * See docs/layersManagement/snapshot-delivery-contract.md.
   */
  layerConfig?: {
    toc?: TOC;
    digitization?: { digitizationLayerIds: string[]; digitizationTOC: TOC };
    specialDatasets?: { datasetConfig: Record<string, { name: string; toc: TOC }> };
    layerIdToDownloadLayer?: Record<string, string>;
  };
  conflictsSummaryConfig: string[];
  zoningAccess: string;
  id: string;
  logo:
    | string
    | {
        path: string;
      };
  downloadsThisMonth: number;
  totalDownloadsPerMonth: number;
  subscription: Subscription;
  /** The org has a current contract or an exempt account type. */
  isSubscriptionActive?: boolean;
  billingContacts: string[];
  allowedDomains: string[];
  useTestEnv?: 'UAT' | null;
  linkedOrganizations?: LinkedOrganizationDetail[];
  defaultRole: string;
  orgAgencyId?: string;
  preferences: {
    defaultProjection?: string;
  };
  /** Axis-aligned bounds of all active projects (PostGIS ST_Envelope(ST_Collect)); null if none. */
  projectBounds: Polygon | null;
  state?: string | null;
}

export enum PlanStatus {
  Pending = 'pending',
  NeedsAttention = 'needsAttention',
  Scheduled = 'scheduled',
}

export interface UtilityNote {
  id: string;
  agency: Agency['id'];
  notes: string;
  createdBy: User['id'];
  updatedBy: User['id'];
  createdAt: DateTime;
  updatedAt: DateTime;
  status: 'active' | 'archived';
}

export interface Project {
  id: string;
  name: string;
  projectId: string;
  organization: string; // just the id of the org
  startDate: DateTime;
  endDate: DateTime;
  geometry: MultiPolygon;
  constructionStartDate?: DateTime;
  constructionEndDate?: DateTime;
  planStatus: PlanStatus;
  projectManager: {
    name: string;
    email: string;
    id?: string;
  };
  projectManagerId?: string;
  programManager?: {
    name: string;
    email: string;
    id?: string;
  } | null;
  programManagerId?: string | null;
  subProgramManager?: {
    name: string;
    email: string;
    id?: string;
  } | null;
  subProgramManagerId?: string | null;
  projectPhase: ProjectPhase;
  projectYear: string;
  constructionYear: string | number;
  createdAtYear: number;
  address: string;
  projectZoningData: FeatureCollection;
  maps: {
    [key: string]: MyMap;
  };
  parcelDetails: ParcelDetails[];
  geometryType?: ProjectGeometryType;
  linear?: {
    buffer: { offset: number; units: Units };
    geometry: GeoJSON.MultiLineString;
  };
  circle?: {
    geometry: GeoJSON.MultiPoint;
    radius: {
      value: number;
      units: 'feet';
    };
  };
  sandboxProject?: boolean;
  createdAt: DateTime;
  updatedAt: DateTime;
  billingCode: string;
  billingCode2: string;
  utilityNotes: UtilityNote[];
  isBillable?: boolean;
  billingTier: 'draft' | 'proposal' | 'project' | 'projectPlus';
  absorbFees: 'none' | 'partial' | 'full';
  status: InternalProjectStatus;
  isConfidential: boolean;
  category: string;
  type: string;
  bookmarked: boolean;
  members: Member[];
  memberString: string;
  memberInitials: string[];
  address2?: {
    street?: string;
    crossStreets?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  metadata?: any;

  // this is returned with the project now
  civilSearchVersion: 'v1' | 'v2';
}

export enum ProjectGeometryType {
  Polygon = 'polygon',
  Linear = 'linear',
  Circle = 'circle',
  Small_3ac = 'small-3ac', // No longer used for new projects, but we still need this type to support existing projects
}

export enum InternalProjectStatus {
  Active = 'active',
  Archived = 'archived',
}

export type FeatureArray = Feature<Geometry | null, GeoJsonProperties>[];

export interface UploadedAndConvertedData {
  validObjects: Feature<Geometry | null, GeoJsonProperties>[];
  invalidObjects: Feature<Geometry | null, GeoJsonProperties>[];
}

// TOC Layers
export interface TOCParent {
  label: string;
  children: TOCItem[];
  expanded: boolean;
  sectionId?: string;
  /** Muted text shown after the label; not searched. */
  labelSuffix?: string;
}

export interface TOCChild {
  id: string;
  label: string;
}

export type TOCItem = TOCParent | TOCChild;

export type TOC = TOCItem[];

export enum EditorMode {
  None = 'None',

  DrawPolygon = 'DrawPolygon',
  DrawLine = 'DrawLine',
  DrawMarker = 'DrawMarker',
  DrawText = 'DrawText',
  DrawTextV2 = 'DrawTextV2',
  DrawImage = 'DrawImage',
  DrawStamp = 'DrawStamp',
  DrawArrow = 'DrawArrow',
  DrawMeasureDistance = 'DrawMeasureDistance',
  DrawMeasureArea = 'DrawMeasureArea',
  DrawCircle = 'DrawCircle',

  EditPolygon = 'EditPolygon',
  EditLine = 'EditLine',
  EditMarker = 'EditMarker',
  EditText = 'EditText',
  EditTextV2 = 'EditTextV2',
  EditImage = 'EditImage',
  EditStamp = 'EditStamp',
  EditArrow = 'EditArrow',
  EditCircle = 'EditCircle',

  EditComment = 'EditComment',
}

interface Region {
  _id: string;
  name: string;
  region: Polygon;
}

interface CurrentOrganization {
  id: string;
  name: string;
  plan: string;
  planStatus: string;
  regions: Region[];
  telcoAccess: boolean;
  zoningAccess: string;
  mobileReportType: MobileReportType;
  orgMemberRole: string;
  specialDatasets: any[];
  specialFeatures: string[];
  telcoData: any[];
}

export interface UserMain {
  me: User;
  currentOrganization: CurrentOrganization;
  token: string;
}

export interface MemberUser {
  email: string;
  id: string;
  name: string;
  status: string;
}
export interface ProjectWithUserData extends Project {
  members: Member[];
}

export interface ProjectWithDataRequests extends Project {
  activeRequestsCount: number;
  activeIncompleteRequestsCount: number;
  isInternal: boolean;
  assignedTo: Array<{
    _id: string;
    name: string;
    email: string;
  }>;
}

export enum MobileReportType {
  PotholeLog = 'potholeLog',
  ManholeInspection = 'manholeInspection',
}

// NOTE: This TS interface is a temporary solution to get the team page working.
interface UserType {
  currentOrganization: CurrentOrganization;
  me: any;
  token: string;
}

export interface RootState {
  user: UserType;
}

export interface GeoItemEdits {
  /** The geo item's `version` when editing started. */
  baseVersion?: number;
  geometry?: Point;
  props?: any[];
  files?: any;
  accuracy?: {
    hrms: number;
    vrms: number | null;
    /** Antenna height in meters when reported by an external receiver; metadata only, displayed in feet. */
    antennaHeight?: number | null;
    solutionType: string;
    receiverMake?: string;
    receiverModel?: string;
    recordedAt?: string;
  };
}

export interface GeoItem {
  createdAt: DateTime;
  createdBy: {
    email: string;
    name: string;
    id: string;
    _id: string;
  };
  files: {
    name: string;
    notes: string;
    path: string;
    thumbnailPath?: string;
    type: string;
    uploadedAt: Date;
    uploadedBy: string;
    compassHeading?: number | null;
  }[];
  geometry: Point;
  id: string;
  project: string;
  // `value` is string | string[]: multi-select props sync from mobile as string arrays.
  props: {
    label: string;
    value: string | string[] | UtilityMeasurementEntry[];
    type: 'string' | 'number' | 'date' | 'select' | 'utilityMeasurements';
  }[];
  fields: {
    [field: string]: {
      label: string;
      value: string | string[];
      type: 'string' | 'number' | 'date' | 'select';
    };
  };
  /**
   * GNSS accuracy and provenance.
   */
  accuracy?: GeoItemElevationAccuracy & {
    hrms: number;
    vrms: number | null;
    /** Antenna height in meters when reported by an external receiver; metadata only, displayed in feet. */
    antennaHeight?: number | null;
    solutionType: string;
    receiverMake?: string;
    receiverModel?: string;
    recordedAt?: string;
    referenceFrameEpoch?: number | null;
    undulation?: number | null;
    measurementMethod?: string | null;
    receiverSerialNumber?: string | null;
  };
  status: string;
  type: 'potholeLog' | 'image' | 'video' | 'manholeInspection' | 'note';
  updatedAt: DateTime;
  updatedBy: string;
  /** Server-stamped, increases on every save. Sent back as `baseVersion` on edit and delete. */
  version?: number;
}

export interface RegionWithId extends Omit<Region, '_id'> {
  id: string;
}

export interface MapBounds {
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
  x: number;
  y: number;
}

export enum TransactionCategory {
  NewProposal = 'NewProposal',
  NewProjectLite = 'NewProjectLite',
  NewProject = 'NewProject',
  NewProjectPlus = 'NewProjectPlus',
  EditProjectBoundary = 'EditProjectBoundary',
  ChainOfTitle = 'ChainOfTitle',
  OpenLienReport = 'OpenLienReport',
  TransactionHistoryReport = 'TransactionHistoryReport',
  AssessorMap = 'AssessorMap',
  UtilityMapRequest = 'UtilityMapRequest',
  Other = 'Other',
  Digitization = 'Digitization',
}

export type TransactionType = 'debit' | 'credit' | 'discount';

export interface NewTransactionData {
  projectId: string;
  description: string;
  amount: number;
  type: TransactionType;
  category: TransactionCategory;
  transactionDate: string;
}

export interface Transaction {
  id: string;
  amount: Dinero.Dinero;
  description: string;
  category: string;
  createdBy: {
    id: string;
    name: string;
    email: string;
  };
  type: TransactionType;
  createdAt: DateTime;
  updatedAt: DateTime;
}

export enum ContactMethod {
  Email = 'email',
  PublicRecordsRequest = 'publicRecordsRequest',
  PortalContactForm = 'portalContactForm',
}

export enum AgencyLinkType {
  Nda = 'nda',
  Letterhead = 'letterhead',
  Other = 'other',
}

export interface AgencyLink {
  id: string;
  title: string;
  description: string;
  url: string;
  type: AgencyLinkType;
  isPrivate: boolean;
  agencySubGeometryId: string | null;
}

export enum DigitizationType {
  Project = 'project',
  Organization = 'organization',
}

interface AgencyDigitizationBase {
  id: string;
  type: DigitizationType;
  createdAt: DateTime;
  updatedAt: DateTime;
  createdBy: User['id'];
  updatedBy: User['id'];
  status: 'active' | 'archived';
}

export interface ProjectAgencyDigitization extends AgencyDigitizationBase {
  type: DigitizationType.Project;
  project: Project;
}

export interface OrgAgencyDigitization extends AgencyDigitizationBase {
  type: DigitizationType.Organization;
  organization: OrganizationDetail;
}

export type AgencyDigitization = ProjectAgencyDigitization | OrgAgencyDigitization;

export enum CivilSearchTier {
  Tier1 = 'tier1',
  Tier2 = 'tier2',
  Tier3 = 'tier3',
  Tier4 = 'tier4',
  Tier5 = 'tier5',
}

export interface AgencyResponseStat {
  id: string;
  agencyId: string;
  /** Null = agency-level stats; non-null rows are scoped to a sub-geometry. */
  agencySubGeometryId: string | null;
  calculatedResponseTime: number; // days
  stats: Record<string, any>;
  // Timestamps arrive as ISO strings (json_build_object serialization), not DateTime.
  calculatedAt: string;
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
}

export interface Agency {
  id: string;
  agencyId: string;
  /** Comma-joined list of this agency's agencyGeometry.internalId values (computed server-side). */
  internalIds: string;
  agencyName: string;
  type: string;
  sourceTable: string;
  isPublished: boolean;
  isDigitized?: boolean;
  assetsInProjectArea?: boolean;
  availableDigitally?: boolean;
  sourceDate?: DateTime;
  lastRefresh?: DateTime;
  asbuiltsAvailable?: boolean;
  // fiber agencies only
  providerId?: string;
  pdfLink?: string | null;
  siteLink?: string | null;
  objectId: number | null;
  // CG-517: Connectbase discovery provenance from the projectAgencies link (dataRequest endpoint).
  discoverySources?: Array<'sharableRoutes' | 'networkIntelligence'> | null;
  discoverySourcesInferred?: boolean;

  // metadata
  doesNotRespond: boolean;
  chargesMapFees: boolean;
  requiresLetterhead: boolean;
  requiresNda: boolean;
  // When true, the customer-facing estimated response time is hidden (Utility tab shows "—").
  hideResponseEstimate: boolean;
  averageMapFeePerPage: number;
  mapFeeNote: string;
  preferredContactMethod: ContactMethod;

  prrUrl: string;
  portalContactFormUrl: string;
  notes: string;
  // Loader-synced outreach/public fields (CG-630, synced from citygrid)
  outreachEmail?: string | null;
  outreachEmailName?: string | null;
  publicNotes?: string | null;
  links: AgencyLink[];
  // CG-474: contacts scoped to the request's sub-geometry, populated only by the
  // data-request edit endpoint when sub-geometry routing is enabled.
  contacts?: AgencyContact[];
  digitization: AgencyDigitization[];
  // Only populated by the /v2/projects/:projectId/agencies payload.
  responseStats?: AgencyResponseStat[];
  state: string;

  civilSearchTier: CivilSearchTier;
  defaultCivilSearchAssigneeId?: string | null;
  defaultCivilSearchAssignee?: User;

  logo?: {
    name: string;
    path: string;
    type: string;
    extension: string;
    notes: string;
    uploadedAt: DateTime;
  };

  createdAt: DateTime;
  updatedAt: DateTime;

  // Merged record this agency was merged into, if any.
  parentAgencyId?: string | null;

  // Merge state derived from parentAgencyId, then agencyMergeMappings. Populated by the
  // admin agency endpoints.
  mergedInto: {
    targetId: string;
    targetAgencyName: string;
    mergedAt: string;
    mergedByName: string;
  } | null;
  hasInboundMerges: boolean;
  // True for a merged record (the parent a merge creates); admin agency endpoints only.
  isMergedRecord?: boolean;

  defaultContact?: AgencyContact['id'];
  restrictions?: {
    urban: number;
    rural: number;
  };

  applicableRestrictions?: {
    urban?: number;
    rural?: number;
  };

  // follow up durations
  firstFollowUpDuration: number; // days
  secondFollowUpDuration: number; // days
  thirdFollowUpDuration: number; // days
  fourthFollowUpDuration: number; // days
  timeoutDuration: number; // days

  status: 'active' | 'archived';

  aiContactResearch: string | undefined;
}

export interface AgencyContact {
  id: string;
  contactId: string;
  agencyName: string;
  type: string;
  notes: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  title: string;
  autoEmail: boolean;
  agency: Agency;
  status: 'active' | 'archived';
  agencySubGeometryId: string | null;
}

export enum RequestStatus {
  Pending = 'pending',
  InProgress0 = 'inProgress0',
  InProgress1 = 'inProgress1',
  InProgress2 = 'inProgress2',
  InProgress3 = 'inProgress3',
  ActionRequired = 'actionRequired',
  OnHoldPerAgency = 'onHoldPerAgency',
  Complete = 'complete',
  Cancelled = 'cancelled',
  Dismissed = 'dismissed',
}

export enum UtilityPresence {
  Present = 'present',
  NoAssetsPresent = 'noAssetsPresent',
  PresenceUnknown = 'presenceUnknown',
  OutsideServiceArea = 'outsideServiceArea',
}

export enum Outcome {
  DocumentsReceived = 'documentsReceived',
  AgencyResponded = 'agencyResponded',
  AgencyNotSharing = 'agencyNotSharing',
  AgencyUnresponsive = 'agencyUnresponsive',
  ReferredToOtherAgency = 'referredToOtherAgency',
}

export enum RequestType {
  MapsRequested = 'mapsRequested',
  AsbuiltsRequested = 'asbuiltsRequested',
}

export interface RequestUpdate {
  id: string;
  notes: string;
  createdBy: User;
  updatedBy: User;
  createdAt: Date;
  updatedAt: Date;
  status: 'active' | 'archived';
}

export interface DataRequestInvoice {
  id: string;
  invoiceId: string;
  agencyInvoiceId: string;
  stripeId: string;
  amount: number;
  isRequestFulfilled: boolean;
  paymentMethod: string;
  requestFulfilledAt?: DateTime;
  paymentToProviderAt?: DateTime;
  customerInvoicedAt?: DateTime;
}

export interface DataRequestV2 {
  id: string;
  requestId: string;
  requestStatus: RequestStatus;
  project: Project;
  agencyId: string;
  // CG-472/474: non-null when the request is scoped to one of the agency's sub-geometries
  // (the agency's contacts/links/notes are resolved to it on the edit surface).
  agencySubGeometryId?: string | null;
  agency: Agency;
  // CG-749: the request's own sub-geometry (internal name) for scope display
  // (getDataRequest only).
  subGeometry?: { id: string; name: string | null } | null;
  // CG-749: the other active requests for the same project + agency (getDataRequest only),
  // so the admin request-details page can surface sub-geometry splits.
  siblings?: {
    id: string;
    requestId: string;
    type: RequestType;
    requestStatus: RequestStatus;
    agencySubGeometryId: string | null;
    agencySubGeometryName: string | null;
    createdAt: string;
  }[];
  type: RequestType;
  notes: string;
  notesInternal: string;
  isFlagged: boolean;
  assignedTo: User;
  files: CivilGridFile[];
  updates: RequestUpdate[];
  sourceTable?: string;
  utilityPresence?: UtilityPresence;
  firstContactedAt?: DateTime;
  secondContactedAt?: DateTime;
  thirdContactedAt?: DateTime;
  fourthContactedAt?: DateTime;
  outcome?: Outcome;
  resolutionDate?: DateTime;
  calledDate?: DateTime;
  nextFollowUpAt?: DateTime;
  invoices: DataRequestInvoice[];
  requestNotes: DataRequestNote[];
  activities: Activity[];
  status: 'active' | 'archived';
  createdAt: DateTime;
  updatedAt: DateTime;
  createdBy: User;
  updatedBy: User;
  mapsRequest?: Pick<DataRequestV2, 'requestStatus' | 'utilityPresence' | 'outcome' | 'type'> | null;
  asbuiltsRequest?: Pick<DataRequestV2, 'requestStatus' | 'utilityPresence' | 'outcome' | 'type'> | null;
}

export interface DataRequestNote {
  id: string;
  note: string;
  userId: string;
  dataRequestId: string;
  user: User;
  createdAt: string;
  updatedAt: string;
}

export interface CivilSearchMessage {
  id: string;
  message: string;
  files: CivilGridFile[];
  createdBy: User;
  updatedBy: User;
  createdAt: Date;
  updatedAt: Date;
}

export interface Activity {
  id?: string;
  activityType: string;
  user?: User;
  organization?: OrganizationDetail;
  project?: Project;
  details?: { [key: string]: any };
  supportingFiles: CivilGridFile[];
  dataRequestFiles: CivilGridFile[];
  createdAt: DateTime;
  updatedAt: DateTime;
  mapFulfillmentRequest?: MapFulfillmentRequest;
}

export enum FileCategory {
  ProjectFile = 'projectFile',
  SupportingFile = 'supportingFile',
  DataRequestFile = 'dataRequestFile',
}

/** Stored-file kinds the project download-URL endpoint signs; Utilities-tab files have their own endpoint. */
export type ProjectStoredFileType = 'project-file' | 'geo-item-media' | 'civildraw-attachment' | 'eris-pdf';

/** Stored-file kinds the admin CivilSearch download-URL endpoint signs. */
export type CivilSearchStoredFileType = 'utility-request-file' | 'supporting-file' | 'civilsearch-message-file';

/** A signed attachment URL for a stored file; `filesize` is in bytes when the server knows it. */
export interface FileDownload {
  url: string;
  filename: string;
  filesize?: number;
}

export interface CivilGridFile {
  id: string;
  name: string;
  path: string;
  type: string;
  extension: string;
  notes: string;
  category: FileCategory;
  uploadedBy: User;
  updatedBy: User;
  createdAt: Date;
  updatedAt: Date;
  project: Project['id'];
}

export enum ConflictRec {
  Accelerate = 'accelerate',
  Defer = 'defer',
  NoScheduleConflict = 'noScheduleConflict',
  NeedsReview = 'needsReview',
}

export interface TPermittingAgency {
  featureId: string;
  name: string;
}

/**
 * One conflict-layer intelligence feature attached to a summary by the v3 list
 * endpoint (accordion child rows). Plan dates arrive parsed server-side to UTC
 * epoch millis (null = missing/unparseable, open-ended for overlap purposes);
 * overlapArea is the feature's right-of-way overlap in sq. ft.
 */
export interface ConflictFeatureRow {
  id: string;
  layerId: string;
  owner?: string | null;
  startMs?: number | null;
  endMs?: number | null;
  overlapArea?: number | null;
  /** Non-null ⟺ the plan window overlaps the construction window, clamped to it. */
  window?: { overlapStartMs: number; overlapEndMs: number } | null;
}

export interface IntelligenceSummary {
  id: string;
  project: Project & { type?: string };
  computeStatus: 'processing' | 'error' | 'valid';
  notes: string;
  hasUndigitizedAgencies: boolean;
  rightOfWayArea?: number;
  gasServiceCount?: number;
  gasDistributionLength?: number;

  // conflict area counts
  pavingProjectsArea?: number;
  pavingMoratoriaArea?: number;
  cipProjectsArea?: number;
  conflictArea?: number;

  moratoriaInfo?: Record<
    string,
    {
      startDate?: DateTime;
      endDate?: DateTime;
      ownerName?: string;
    }
  >;

  pavingProjectsInfo?: Record<
    string,
    {
      startDate?: DateTime;
      endDate?: DateTime;
      ownerName?: string;
    }
  >;

  cipProjectsInfo?: Record<
    string,
    {
      startDate?: DateTime;
      endDate?: DateTime;
      ownerName?: string;
    }
  >;

  // jurisdiction counts
  incorporatedPlacesCount?: number;
  countyCount?: number;
  landUseTransportationDistrictBoundariesCount?: number;

  // right of way crossings counts
  landUseRailroadsCount?: number;
  transitLinesCount?: number;
  landUseRoadsBufferCount?: number;

  undigitizedAgencies: Agency[];
  conflictFeatures?: ConflictFeatureRow[];
  /** SCE column values keyed by column id, resolved by the v3 list endpoint (sce-configured orgs only). */
  sce?: Record<string, string | number>;
  /** Feature-grain Conflict Detail counts + clamped overlap range, computed by the v3 list endpoint. */
  conflictWindow?: {
    counts: { moratoria: number; paving: number; cip: number };
    overlapStartMs: number;
    overlapEndMs: number;
  } | null;
  updatedBy?: User['id'];

  schedule: {
    recommendation: ConflictRec;
    conflictsStart?: DateTime;
    conflictsEnd?: DateTime;
  };

  // for PG&E
  pge?: {
    region?: string;
    division?: string;
  };

  // for SCE
  // permitting - these need to be arrays since multiple can exist
  incorporatedPlaces: TPermittingAgency[];
  counties: TPermittingAgency[];
  landUseTransportationDistrictBoundaries: TPermittingAgency[];
  environmentalFederalLands?: { agencyName?: string; unitName?: string }[];

  /* ---------- Environmental ---------- */
  exclusionProtectedAreaDatabaseHoldingsCPADPresent?: boolean;
  exclusionUSGSProtectedAreasPresent?: boolean;

  environmentalWetlandsPresent?: boolean;

  exclusionLandHeldInConservationEasementCCEDPresent?: boolean;

  environmentalHazardousWastePresent?: boolean;
  environmentalGeoTrackerSitesPresent?: boolean;

  californiaStateParkBoundariesPresent?: boolean;
  federallyRecognizedTribalLandsPresent?: boolean;

  landUseRoadsStateHighwayPresent?: boolean;

  environmentalLocalCoastalProgramPresent?: boolean;

  landRightsParcelsCount?: number;
}

/** Raw `properties` blob of a conflict-layer intelligence row, surfaced verbatim (see PSCO-137). */
export type ProjectConflictDetails = Record<string, unknown>;

/**
 * One conflict-layer intelligence row from GET /v3/projects/:projectId/intelligence.
 * Dates are ISO strings (JSON), not the legacy IntelligenceSummary's DateTime shape.
 */
export interface ProjectConflict {
  id: string;
  layerId: string;
  featureId: string;
  projectId: string;
  rgb: [string, string, string];
  geometry: GeoJSON.Geometry;
  details: ProjectConflictDetails;
  startDate: string | null;
  endDate: string | null;
  area: number;
  projectRightOfWayOverlapArea: number; // overlap between this feature and the project right-of-way, in sq ft
  projectRightOfWayOverlapAreaPercentage: number; // right-of-way overlap as a percentage of the full project area
  status: string;
  agencyId: string;
  createdAt: string;
  updatedAt: string;
}

/** Conflict-agency row without geometry; remaining agency columns come through as JSON. */
export interface ProjectConflictAgency {
  id: string;
  agencyId: string | null;
  agencyName: string;
  type: string;
  sourceTable: string;
  isPublished: boolean;
  sourceDate: string | null;
  lastRefresh: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  researchStatus: string;
  [key: string]: unknown;
}

/**
 * A raw intelligenceV2 row from GET /v3/projects/:projectId/intelligence, grouped by layer.
 * Mirrors the server `TIntelligenceV2` row; dates are ISO strings (JSON), not Date instances.
 */
export interface IntelligenceV2Item {
  id: string;
  layerId: string;
  featureId: string;
  projectId: string | null;
  rgb: string[];
  reviewStatus: string;
  geometry: GeoJSON.Geometry;
  properties: Record<string, unknown>;
  lastSyncedAt: string;
  updatedById: string | null;
  calculations: {
    area?: number;
    overlapArea?: number;
    overlapPercentage?: number;
    boundaryOverlapPercentage?: number;
  } | null;
  archivedAt: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export enum DrfRequestStatus {
  PaymentPending = 'paymentPending',
  Pending = 'pending',
  AutoFulfilled = 'autoFulfilled',
  Fulfilled = 'fulfilled',
  NeedsReview = 'needsReview',
  Rejected = 'rejected',
  Cancelled = 'cancelled',
}

export interface RequestStatusTransition {
  id: string;
  from: DrfRequestStatus;
  to: DrfRequestStatus;
  timeInStatus: number;
  updatedBy: User;
  createdAt: DateTime;
  updatedAt: DateTime;
  status: 'active' | 'archived';
}

export enum RequestFlag {
  BlockedDomain = 'blockedDomain',
  ExceedsLengthLimit = 'exceedsLengthLimit',
  ExceedsAreaLimit = 'exceedsAreaLimit',
  CnpCriticalInfrastructure = 'cnpCriticalInfrastructure',
}

export interface MapFulfillmentRequest {
  id: string;
  requestId: string;
  agency: Agency;
  name: string;
  notes?: string;
  client: string;
  scope: string;
  startDate?: DateTime;
  endDate?: DateTime;
  externalRefId: string;
  sharedLinkUrl: string;
  isPublicProject: boolean;
  publicProjectInfo: {
    [field: string]: {
      label: string;
      type: 'string' | 'boolean';
      value: string | boolean;
    };
  };
  paymentMethod?: {
    type: 'waived' | 'card' | 'other';
    id: string;
    customerId: string;
  };
  requestor: {
    name: string;
    email: string;
    phone: string;
    organization: string;
    address: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
    };
  };
  geometryType: 'polygon' | 'linear';
  geometry: MultiPolygon;
  areaInAcres: number;
  linear?: {
    geometry: LineString | MultiLineString;
    buffer: {
      offset: number; // in feet
      units: 'feet';
    };
    lengthInMiles: number;
  };
  requestStatus: DrfRequestStatus;
  requestFlags: RequestFlag[];
  completedBy?: User;
  completedAt?: DateTime;
  ceiiFile?: {
    name: string;
    path: string;
    /** Signed as an attachment, so following it saves the file under `name`. */
    downloadPath?: string;
    type: string;
    extension: string;
    notes: string;
    uploadedAt: Date;
    // don't track uploadedBy since requestor isn't necessarily a "User"
  };
  status: 'active' | 'archived';
  createdAt: DateTime;
  updatedAt: DateTime;
  statusTransitions: RequestStatusTransition[];
}

export interface AdminTileLayer {
  key: string;
  label: string;
  category: string;
  enabled: boolean;
  specialDatasetSlug: string | null;
  sourceUrl: string;
}
